/**
 * ErrorBoundary — catches render-time errors and shows a calm
 * recovery screen instead of white-screening the whole app.
 */

import { Component, type ErrorInfo, type ReactNode } from "react";

interface Props {
  children: ReactNode;
}

interface State {
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Log to console — once we wire prompt-logs/observability on the server
    // we can also POST a summary to /api/client-errors.
    console.error("[ErrorBoundary]", error, info);
  }

  handleReset = () => {
    this.setState({ error: null });
  };

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <main className="page-narrow" role="alert">
        <span className="sc sc-accent">Something went wrong</span>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "var(--text-2xl)",
            fontWeight: 600,
            marginTop: "var(--space-2)",
            letterSpacing: "var(--track-display)",
            color: "var(--ink)",
          }}
        >
          The page didn't load as expected.
        </h1>
        <p className="text-muted" style={{ marginTop: "var(--space-3)", lineHeight: "var(--lh-normal)" }}>
          A client-side error prevented the current view from rendering. You can try again, or reload the
          page. If this keeps happening, let us know what you were doing.
        </p>
        <pre
          style={{
            marginTop: "var(--space-4)",
            padding: "var(--space-3)",
            background: "var(--rule-soft)",
            borderRadius: "var(--radius-md)",
            fontFamily: "var(--font-mono)",
            fontSize: "var(--text-xs)",
            color: "var(--muted)",
            overflow: "auto",
            maxHeight: 240,
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
          }}
        >
          {this.state.error.message}
        </pre>
        <div className="form-actions">
          <button className="btn btn-primary" onClick={this.handleReset}>Try again</button>
          <button className="btn btn-ghost" onClick={() => window.location.reload()}>Reload page</button>
        </div>
      </main>
    );
  }
}
